import { clearSupportDrafts } from './supportDraft'
import type { SessionResponse } from '../types'

const configuredApiBaseUrl = (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_PLATFORM_API_URL || '/api').replace(/\/$/, '')
// Local development goes through Vite so the platform session cookie and CSRF
// exchange stay same-origin. Production keeps the explicit API origin.
const apiBaseUrl = import.meta.env.DEV && /^http:\/\/localhost:\d+$/.test(configuredApiBaseUrl)
  ? '/api'
  : configuredApiBaseUrl
let accessToken: string | null = null
let csrfToken: string | null = null
let refreshPromise: Promise<string | null> | null = null
const listeners = new Set<() => void>()

export const platformApiUrl = apiBaseUrl
export const getAccessToken = () => accessToken
export const subscribeSession = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } }

function emitSessionChange() { listeners.forEach(listener => listener()) }

export function setSession(session: SessionResponse | null) {
  if (!session) clearSupportDrafts()
  accessToken = session?.access ?? null
  emitSessionChange()
}

async function parseJson(response: Response): Promise<unknown> {
  try { return await response.json() } catch { return null }
}

export async function ensureCsrf(force = false) {
  if (csrfToken && !force) return csrfToken
  const response = await fetch(`${apiBaseUrl}/v1/platform-access/auth/csrf/`, { credentials: 'include', cache: 'no-store' })
  const body = await parseJson(response) as { csrf_token?: string } | null
  if (!response.ok || !body?.csrf_token) throw new Error('Não foi possível iniciar a sessão segura.')
  csrfToken = body.csrf_token
  return csrfToken
}

export async function refreshSession(): Promise<string | null> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const csrf = await ensureCsrf()
      const response = await fetch(`${apiBaseUrl}/v1/platform-access/auth/refresh/`, {
        method: 'POST', credentials: 'include', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf }, body: '{}',
      })
      if (response.status === 403) {
        const retryCsrf = await ensureCsrf(true)
        const retry = await fetch(`${apiBaseUrl}/v1/platform-access/auth/refresh/`, {
          method: 'POST', credentials: 'include', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': retryCsrf }, body: '{}',
        })
        if (!retry.ok) { setSession(null); return null }
        const session = await retry.json() as SessionResponse
        setSession(session)
        return session.access
      }
      if (!response.ok) { setSession(null); return null }
      const session = await response.json() as SessionResponse
      setSession(session)
      return session.access
    } catch { setSession(null); return null }
    finally { refreshPromise = null }
  })()
  return refreshPromise
}

export async function request(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  const method = (init.method || 'GET').toUpperCase()
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('X-CSRFToken', await ensureCsrf())
  let response = await fetch(`${apiBaseUrl}${path}`, { ...init, headers, credentials: 'include', cache: 'no-store' })
  if (response.status === 403 && method !== 'GET') {
    const clone = response.clone()
    const detail = await parseJson(clone) as { detail?: string } | null
    if (/csrf/i.test(detail?.detail || '')) {
      headers.set('X-CSRFToken', await ensureCsrf(true))
      response = await fetch(`${apiBaseUrl}${path}`, { ...init, headers, credentials: 'include', cache: 'no-store' })
    }
  }
  if (response.status === 401 && retry && path !== '/v1/platform-access/auth/refresh/') {
    const token = await refreshSession()
    if (token) return request(path, init, false)
    setSession(null)
  }
  return response
}

export async function logoutSession() {
  try {
    if (!accessToken) await refreshSession()
    const csrf = await ensureCsrf(true).catch(() => null)
    await fetch(`${apiBaseUrl}/v1/platform-access/auth/logout/`, {
      method: 'POST', credentials: 'include', headers: {
        Accept: 'application/json', 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRFToken': csrf } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      }, body: '{}', cache: 'no-store',
    })
  } finally { setSession(null) }
}
